<template>
  <div class="fileUpload">
    <el-upload
      ref="upload"
      :class="['upload', { 'upload-box': type === 'box', 'upload-input': type === 'input' }]"
      :disabled="disabled"
      action=""
      :auto-upload="false"
      :show-file-list="false"
      :limit="1"
      :accept="accept"
      :drag="type === 'box'"
      :on-change="handleUploadChange"
    >
      <template v-if="type === 'box'">
        <i class="el-icon-upload upload-icon"></i>
        <div class="el-upload__text">
          {{ $t('fileUpload.dragHere') }}<em>{{ $t('fileUpload.clickToUpload') }}</em>
          <div class="upload-placeholder">
            {{placeholder}}
          </div>
        </div>
        <div
          :class="['upload-file-name', `upload-file-name-${status}`]"
          v-if="fileName"
          slot="tip"
        >
          <i class="el-icon-paperclip"></i>
          <span>
            {{fileName}}
          </span>
        </div>
      </template>
      <template v-if="type === 'input'">
        <el-input
          class="upload-input"
          :value="fileName"
          suffix-icon="el-icon-upload"
          :placeholder="placeholder"
          readonly
        ></el-input>
      </template>
      <template v-if="type==='button'">
        <el-button
          class="upload-button"
          type="primary"
          icon="el-icon-upload"
        >{{placeholder}}</el-button>
        <div
          :class="['upload-file-name', `upload-file-name-${status}`]"
          v-if="fileName"
          slot="tip"
        >
          <i class="el-icon-paperclip"></i>
          <span>
            {{fileName}}
          </span>
          <el-button
            @click="handleClear"
            type="text"
            class="deleteIconBtn"
            icon="el-icon-delete"
          />
        </div>
      </template>
    </el-upload>
  </div>
</template>

<script>
import { uploadFile } from '@/api/base';
import { formatFileSize } from '@/utils/file-size';

export default {
  name: 'FileUpload',
  props: {
    type: {
      type: String,
      default: 'input',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: function () {
        return this.$t('fileUpload.uploadFile');
      },
    },
    accept: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    maxSize: {
      type: Number,
      default: 1024 * 1024 * 500, // 500MB
    },
  },
  data() {
    return {
      fileName: this.name,
      status: 'init',
    };
  },
  methods: {
    handleUploadChange(file) {
      this.fileName = file.name;
      this.status = 'init';
      this.$emit('start', { fileName: file.name });

      if (file.size > this.maxSize) {
        const sizeStr = formatFileSize(this.maxSize);
        this.status = 'error';
        this.$emit('end', 'error', { message: this.$t('fileUpload.fileSizeExceeded', { maxSize: sizeStr }) });
        this.$refs.upload.clearFiles();
        return;
      }

      uploadFile({ file: file.raw })
        .then((res) => {
          this.status = 'success';
          this.$emit('end', 'success', res);
        })
        .catch((err) => {
          this.status = 'error';
          this.$emit('end', 'error', err);
        })
        .finally(() => {
          this.$refs.upload.clearFiles();
        });
    },
    clear() {
      this.$refs.upload.clearFiles();
      this.fileName = '';
      this.status = 'init';
    },
    handleClear() {
      this.clear();
      this.$emit('clear');
    },
  },
};
</script>

<style lang="scss">
.fileUpload {
  .upload {
    .el-upload {
      display: block !important;

      text-align: center;
      cursor: pointer;
      position: relative;
      overflow: hidden;

      &:hover {
        border-color: #409eff;
      }
    }

    .el-upload__text {
      font-size: 14px;
      color: #606266;

      em {
        color: #409eff;
        font-style: normal;
      }
    }

    .upload-icon {
      font-size: 67px;
      color: #c0c4cc;
      margin: 32px 0 16px;
      line-height: 50px;
    }
  }

  .upload-button {
    width: 100%;
    background: #67c23a !important;
    border-color: #67c23a !important;
    padding: 0;
    height: 40px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 16px;

    .el-icon-upload {
      font-size: 24px;
    }
  }

  .upload-placeholder {
    width: 60%;
    margin: 10px auto;
    font-size: 12px;
    color: #909399;
  }

  .upload-file-name {
    margin-top: 8px;
    display: flex;
    align-items: center;
    color: #909399;

    .deleteIconBtn {
      margin-left: auto;
    }

    &:hover .deleteIconBtn {
      display: inline-block;
    }

    &.upload-file-name-success {
      color: #67c23a;
    }

    &.upload-file-name-error {
      color: #f56c6c;
    }

    i {
      margin-right: 8px;
    }
  }

  .upload-box {
    .el-upload-dragger {
      width: 100%;
    }
    .el-upload {
      border-radius: 6px;
      box-sizing: border-box;
      height: 180px;
    }
  }

  .upload-input {
    .el-icon-upload {
      font-size: 24px;
    }
  }

  .upload-input.el-input .el-input__inner {
    cursor: pointer;
    background-color: #f5f7fa;
    border-color: #e4e7ed;
  }

  .el-icon-paperclip {
    color: #212121;
  }
}
</style>